import { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTimes, faExclamationTriangle, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons"
import { Button, Col, Form, Row, Modal, Dropdown } from "react-bootstrap"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { date, z } from "zod"
import { toast } from "sonner"
import { DeleteRequest, GetRequest, PostRequest, PutRequest } from "../../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import Select from "react-select"
import { useNavigate } from "react-router-dom"
import nodata from "../../../Utils/assets/img/leads_no_data_found.png"
// timezone lib
import TimezoneSelect, { allTimezones } from "react-timezone-select"
import { useGetRequest } from "../../../Utils/hooks/QueryHandler"
import DeleteModal from "../../../Components/DeleteModal"
import { customStyles } from "../../../Utils/helpers/commonhelper"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { IconPoint } from "@tabler/icons-react"
import { UnauthViewModal } from "../../../pages/UnauthViewModal"
import { useOutsideClickHandler } from "../../../Utils/hooks/useOutsideClickHandler"
// Updated Zod schema to match your API requirements
const meetingSchema = z
  .object({
    assign_to: z.string().optional(),
    meeting_title: z.string().min(1, "Meeting Title is Required"),
    meeting_date: z.string().min(1, "Meeting date is required"),
    meeting_to: z.string(),
    meeting_from: z.string(),
    meeting_type: z.string(),
    meeting_link: z.string().optional(),
    address: z.string().optional(),
    description: z.string().optional(),
    meeting_status: z.string(),
  })
  .refine(
    (data) => {
      if (data.meeting_type === "virtual") {
        data.address = ""
        return data.meeting_link && data.meeting_link.trim() !== ""
      }
      return true
    },
    {
      message: "Meeting link is required for virtual meetings",
      path: ["meeting_link"],
    },
  )
  .refine(
    (data) => {
      if (data.meeting_type !== "virtual") {
        data.meeting_link = ""
        return data.address && data.address.trim() !== ""
      }
      return true
    },
    {
      message: "Address is required for non-virtual meetings",
      path: ["address"],
    },
  )

const formatDateForAPI = (date) => {
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}
const cleanTimeFormat = (timeString) => {
  if (!timeString) return ""

  // Remove AM/PM from 24-hour format times
  return timeString.replace(/ (AM|PM)$/i, "")
}
// /**
//  * MeetingsComponent - Displays and manages meetings for a lead
//  */
export const Meetings = ({ leadId, email, fullName, module_name, meetingGrant }) => {
  const [customer, setCustomer] = useLocalStorageState('customer')
  const [meetings, setMeetings] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editMeetingForm, setEditMeetingForm] = useState(false)
  const [editMeetingData, setEditMeetingData] = useState(null)
  const [isPosting, setIsPosting] = useState(false)
  // cancelled loader state
  const [isCancelled, setIsCancelled] = useState(false)
  const [leadOwner, setLeadOwner] = useState([
    {
      leadName: "",
      leadId: "",
    },
  ])

  const [meetingFilter, setMeetingFilter] = useState("today")
  // state for current meeting listing
  const [currentMeeting, setCurrentMeeting] = useState("This Week Meeting")
  const [endpoint, setEndpoint] = useState("")
  const [queryKey, setQueryKey] = useState("")
  // toggling state for delete modal
  const [confirmDeleteMeeting, setConfirmDeleteMeeting] = useState(false)
  // delete meeting id state
  const [deleteMeetingId, setDeleteMeetingId] = useState(null)
  // ref for email dropdown
  const emailDropdownRef = useRef(null)
  // all the email recipients that would be stored by default
  const [emailRecipients, setEmailRecipients] = useState([])
  const [currentEmailInput, setCurrentEmailInput] = useState("")
  // state for email tag dropdown
  const [showEmailDropdown, setShowEmailDropdown] = useState(false)
  // to store the current user email input (only the user object)
  const [emailOptions, setEmailOptions] = useState(null)
  const [isLoadingEmails, setIsLoadingEmails] = useState(false)
  // delete modal loading state
  const [loading, setLoading] = useState(false)
  // states for storing the email search based on which api would get called
  const [emailSearchInput, setEmailSearchInput] = useState("")
  const [debounceEmailSearchInput, setDebounceEmailSearchInput] = useState("")

  // organizer availability warning state
  const [hasConflict, setHasConflict] = useState(false)
  const [hasShownWarning, setHasShownWarning] = useState(false)

  const [expandedTitles, setExpandedTitles] = useState({});

  // warning message state
  const [warningMessage, setWarningMessage] = useState("")
  // timezone state
  const [selectedTimezone, setSelectedTimezone] = useState({
    value: "Asia/Kolkata",
    label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
    offset: 5.5,
    abbrev: "IST",
    altName: "India Standard Time",
  })
  const navigate = useNavigate()
  const headers = {
    Authorization: customer?.token?.authorization,
    Authentication: customer?.token?.authentication,
  }

  // for calendar dates
  const [currentDate, setCurrentDate] = useState(new Date())
  const [visibleStartDate, setVisibleStartDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(new Date().getDate())

  const [selectedDateForApi, setSelectedDateForApi] = useState(new Date())
  const extras = {
    customer,
    setCustomer,
    rf: customer?.token?.authentication,
    navigate,
  }

  const meetingTypeOptions = [
    { value: "inperson", label: "In-Person" },
    { value: "virtual", label: "Virtual" },
  ]

  const meetingStatusOptions = [
    { value: "scheduled", label: "Scheduled" },
    { value: "conducted", label: "Conducted" },
    { value: "missed", label: "Missed" },
    { value: "cancelled", label: "Cancelled" },
  ]

  // Function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date()
    return now.toTimeString().slice(0, 5)
  }

  // Function to add 30 minutes to a time string
  const addThirtyMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    date.setMinutes(date.getMinutes() + 30)
    return date.toTimeString().slice(0, 5)
  }

  // to add new email to the tag
  const addEmailTag = (emailToAdd = null) => {
    const emailValue = emailToAdd || currentEmailInput.trim()
    if (emailValue && !emailRecipients.includes(emailValue)) {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (emailRegex.test(emailValue)) {
        setEmailRecipients((prev) => [...prev, emailValue])
        setCurrentEmailInput("")
        setShowEmailDropdown(false)
      } else {
        toast.error("Please enter a valid email address")
      }
    }
  }
  //  to remove email
  const removeEmailTag = (emailToRemove) => {
    if (emailToRemove === email) {
      return
    }
    setEmailRecipients((prev) => prev.filter((email) => email !== emailToRemove))
    setDebounceEmailSearchInput("")
  }

  const handleEmailInputChange = (e) => {
    const value = e.target.value
    setCurrentEmailInput(value)
    setEmailSearchInput(value)

    // Show dropdown when user starts typing
    if (value.length > 0) {
      setShowEmailDropdown(true)
    } else {
      setShowEmailDropdown(false)
    }
  }
  // on enter click email gets added
  const handleEmailInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addEmailTag()
    } else if (e.key === "Backspace" && currentEmailInput === "" && emailRecipients.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeEmailTag(emailRecipients[emailRecipients.length - 1])
    }
  }

  const setCurrentTimeValues = () => {
    const currentTime = getCurrentTime()
    const endTime = addThirtyMinutes(currentTime)

    setValue("meeting_from", currentTime)
    setValue("meeting_to", endTime)
  }
  // React Hook Form setup with Zod validation
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue,
  } = useForm({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      meeting_title: "",
      meeting_date: today,
      meeting_to: addThirtyMinutes(getCurrentTime()),
      meeting_from: getCurrentTime(),
      address: "",
      assign_to: customer.customer_details?.id,
      meeting_type: "inperson",
      meeting_link: "",
      description: "",
      meeting_status: "scheduled",
    },
  })

  const toggleTitle = (id) => {
    setExpandedTitles(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const truncate = (text, limit = 40) =>
    text.length > limit ? text.substring(0, limit) + "" : text;

  useEffect(() => {
    if (email) {
      setEmailRecipients([email])
    }
  }, [email])

  // useeffect for outside click of the email dropdown modal
  useOutsideClickHandler({
    objRef: emailDropdownRef,
    isDropdown: showEmailDropdown,
    setDropdown: setShowEmailDropdown
  })
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (
  //       emailDropdownRef.current &&
  //       !emailDropdownRef.current.contains(event.target) &&
  //       // Also check if the click is not on the email input itself
  //       event.target.id !== "email-input"
  //     ) {
  //       setShowEmailDropdown(false)
  //     }
  //   }

  //   document.addEventListener("mousedown", handleClickOutside)
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside)
  //   }
  // }, [])

  // useEffect for debouncing the invitees email search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceEmailSearchInput(emailSearchInput)
    }, 300)

    return () => clearTimeout(timer)
  }, [emailSearchInput])

  // useeffect for fetching all the existing emails
  useEffect(() => {
    ; (async () => {
      try {
        setIsLoadingEmails(true)
        const res = await GetRequest(
          `lead/getAllEmailWithNameForMeeting/${customer.customer_details?.customer_id}?search=${debounceEmailSearchInput}`,
          { headers },
          extras,
        )
        if (res.status === "fail") {
          toast.error(res.message)
          return
        }
        if (res.success === false) {
          toast.error(res.message)
          return
        }
        if (res) {
          setEmailOptions(res)
        }
      } catch (error) {
      } finally {
        setIsLoadingEmails(false)
      }
    })()
  }, [debounceEmailSearchInput])

  // useeffect for fetching the owners
  useEffect(() => {
    ; (async () => {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customer.customer_details.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      )
      if (res.err) {
        toast.error(res.message)
      }
      const owners =
        res.data &&
        res.data.map((lead) => ({
          leadName: lead.full_name,
          leadId: lead.id,
        }))
      setLeadOwner(owners)
    })()
  }, [])

  const meetingType = watch("meeting_type")

  // usegetreq for fetching the meetings data
  useEffect(() => {
    if (editMeetingData && editMeetingForm) {
      // Reset the form with the edit data
      reset({
        meeting_title: editMeetingData.meeting_title || "",
        meeting_date: editMeetingData.meeting_date || "",
        meeting_to: cleanTimeFormat(editMeetingData.meeting_to) || addThirtyMinutes(getCurrentTime()),
        meeting_from: cleanTimeFormat(editMeetingData.meeting_from) || getCurrentTime(),
        address: editMeetingData.address || "",
        meeting_type: editMeetingData.meeting_type || "inperson",
        meeting_link: editMeetingData.meeting_link || "",
        description: editMeetingData.description || "",
        meeting_status: editMeetingData.meeting_status || "scheduled",
        assign_to: editMeetingData.assign_to || customer.customer_details?.id,
      })

      // Set timezone if available in edit data
      if (editMeetingData.timezone) {
        setSelectedTimezone(JSON.parse(editMeetingData.timezone))
      }

      // Set email recipients if available
      if (editMeetingData.invitees) {
        const parsedMails = JSON.parse(editMeetingData.invitees)
        const emails = parsedMails.map((invitee) => invitee.email)
        setEmailRecipients(emails)
      }
    }
  }, [editMeetingData])

  // useEffect(() => {
  //   // fetching contacts based on header
  //   const lowerHeading = currentMeeting ? currentMeeting?.trim().toLowerCase()?.split(" ").join("_") : currentMeeting
  //   if (lowerHeading === "today_meeting") {
  //     setEndpoint(`lead/fetchTodaysMeeting/${leadId}/${customer.customer_details?.customer_id}`)
  //     setQueryKey("today_meeting")
  //   } else if (lowerHeading === "tomorrow_meeting") {
  //     setEndpoint(
  //       `lead/fetchTomarrowMeeting//${customer.customer_details?.id}/${customer.customer_details?.customer_id}/today`,
  //     )
  //     setQueryKey("tommorrow_meeting")
  //   } else if (lowerHeading === "this_week_meeting") {
  //     setEndpoint(`lead/fetchThisWeekMeeting/${leadId}/${customer.customer_details?.customer_id}/month`)
  //     setQueryKey("this_week_meeting")
  //   } else if (lowerHeading === "this_month_meeting") {
  //     setEndpoint(`lead/fetchThisMonthMeeting/${leadId}/${customer.customer_details.customer_id}`)
  //     setQueryKey("this_month_meeting")
  //   }
  // }, [currentMeeting])

  const formattedDate =
    meetingFilter === "today" ? formatDateForAPI(selectedDateForApi) : ""

  const {
    data,
    error,
    refetch: meetingRefetch,
    isLoading: meetingIsLoading,
  } = useGetRequest(
    `/lead/fetchMeetings/${leadId}/${customer.customer_details?.customer_id}/${module_name}${formattedDate !== "" ? `?date=${formattedDate}` : ""}`,
    ["meetingsQuery", selectedDateForApi, formattedDate], // Add selectedDateForApi as a dependency
    headers,
    { customer, setCustomer, rf: headers.Authentication, navigate }
  )
  // useeffect for fetching meeting data
  useEffect(() => {
    if (data && data.data) {
      setMeetings(data.data)
    }
  }, [data])

  // warning check handler across assigned to
  const handleWarningCheck = async ({ action, meet_id, assign_to_id, date, time }) => {
    const payload = {
      action: action,
      meeting_id: meet_id || null,
      customer_id: customer.customer_details?.customer_id,
      assign_to: assign_to_id,
      meeting_date: date,
      meeting_from: time,
    }
    try {
      const warningRes = await PostRequest("lead/checkAlreadyMeeting_schedule", payload, false, { headers }, extras)
      if (warningRes.status === "fail") {
        return false
      }
      setWarningMessage(warningRes.message)
      return true
    } catch (error) {
      toast.error("Error checking schedule!")
      return false
    }
  }

  /**
   * Handles adding a new meeting
   */
  const invitees =
    emailOptions !== null
      ? emailRecipients
        .map((recipientEmail) => {
          // Search across all modules (user, contact, etc.)
          for (const [module, list] of Object.entries(emailOptions)) {
            if (recipientEmail === email) {
              return {
                email: email,
                fullName: fullName,
                id: leadId,
                module: "contacts",
              }
            } else {
              const found = list.find((item) => item.email === recipientEmail)
              if (found) {
                return {
                  ...found, // id, name, email already in found
                  module, // add the module dynamically
                }
              } else {
                return {
                  email: recipientEmail,
                  fullName: recipientEmail?.split("@")[0],
                  id: 0,
                  module: "guest",
                }
              }
            }
          }
          return null // if not found
        })
        .filter(Boolean) // remove nulls
      : []
  const onSubmit = async (data) => {
    if (!meetingGrant?.isCreate) {
      toast.warning("You are not authorized for this action!")
      return
    }
    // Format the data for display in the UI
    const meeting_payload = {
      module_id: leadId,
      module_name: module_name,
      customer_id: customer.customer_details?.customer_id,
      ...data,
      invitees: invitees,
      timezone: selectedTimezone,
      user_login_id: customer.customer_details?.id,
    }

    // Check for conflicts but only show warning once
    const conflictExists = await handleWarningCheck({
      action: "create",
      meet_id: null,
      assign_to_id: data.assign_to,
      date: data.meeting_date,
      time: data.meeting_from,
    })

    if (conflictExists && !hasShownWarning) {
      // Show warning but don't prevent submission
      setHasConflict(true)
      setHasShownWarning(true)
      // Continue with creation despite the warning
      return
    } else {
      setHasConflict(false)
    }

    // Proceed with meeting creation regardless of conflict
    setIsPosting(true)
    try {
      const res = await PostRequest(`lead/createMeeting`, meeting_payload, false, { headers }, extras)

      if (res.status === "fail") {
        toast.error(res.message)
        return
      }
      if (res.success === false) {
        toast.error(res.message)
        return
      }

      // Reset form and states
      reset()
      setSelectedTimezone({
        value: "Asia/Kolkata",
        label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
        offset: 5.5,
        abbrev: "IST",
        altName: "India Standard Time",
      })
      setEmailRecipients(email ? [email] : [])
      meetingRefetch()
      setShowAddForm(false)
      setEditMeetingForm(false)
      setEditMeetingData(null)
      setHasShownWarning(false) // Reset warning state
      setHasConflict(false)
      toast.success(res.message)
    } catch (e) {
      toast.error("Please Try Re-Scheduling")
    } finally {
      setIsPosting(false)
    }
  }

  const handleUpdateMeeting = async (data) => {
    const meeting_payload = {
      module_id: leadId,
      meeting_id: editMeetingData.id,
      module_name: module_name,
      ...data,
      customer_id: customer.customer_details?.customer_id,
      invitees: invitees,
      timezone: selectedTimezone,
      user_login_id: customer.customer_details?.id,
    }
    // Check for conflicts but only show warning once
    const conflictExists = await handleWarningCheck({
      action: "create",
      meet_id: null,
      assign_to_id: data.assign_to,
      date: data.meeting_date,
      time: data.meeting_from,
    })

    if (conflictExists && !hasShownWarning) {
      // Show warning but don't prevent submission
      setHasConflict(true)
      setHasShownWarning(true)
      // Continue with creation despite the warning
      return
    } else {
      setHasConflict(false)
    }

    setIsPosting(true)
    try {
      const res = await PutRequest(`lead/updateMeeting/${editMeetingData.id}`, meeting_payload, { headers }, extras)

      if (res.status === "fail") {
        toast.error(res.message)
        return
      }
      if (res.success === false) {
        toast.error(res.message)
        return
      }
      reset({
        meeting_title: "",
        meeting_date: "",
        meeting_to: addThirtyMinutes(getCurrentTime()),
        meeting_from: getCurrentTime(),
        address: "",
        meeting_type: "inperson",
        meeting_link: "",
        description: "",
        meeting_status: "scheduled",
        assign_to: customer.customer_details?.id,
      })
      setEmailRecipients(email ? [email] : [])
      setSelectedTimezone({
        value: "Asia/Kolkata",
        label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
        offset: 5.5,
        abbrev: "IST",
        altName: "India Standard Time",
      })
      meetingRefetch()
      setEditMeetingForm(false)
      setEditMeetingData(null)
      setHasShownWarning(false)
      toast.success(res.message)
    } catch (error) {
      toast.error("Update Failed. Please Try Again!")
    } finally {
      setIsPosting(false)
    }
  }

  // handle to cancel the meeting
  const handleCancelMeeting = async (meetingId) => {
    try {
      setIsCancelled(true)
      if (!meetingId) return
      const payload = {
        meeting_id: meetingId,
        user_login_id: customer.customer_details?.id,
        module_name: "leads",
        customer_id: customer.customer_details?.customer_id,
      }
      const res = await PutRequest("lead/cancelMeeting", payload, { headers }, extras)
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message)
        return
      }
      meetingRefetch()
      setIsCancelled(false)
      toast.success(res.message)
    } catch (error) {
      toast.error("Failed!")
    }
  }
  /**
   * Handles meeting edit action
   */
  const handleEditMeeting = async (meetingId, module_id) => {
    if (!meetingGrant?.isEdit) {
      toast.warning("You are not authorized for this action!")
      return
    }
    try {
      const res = await GetRequest(`lead/getMeetingDetailsById/${meetingId}/${module_id}`, { headers }, extras)
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message)
        return
      }
      setEditMeetingData(res.data?.[0])
      setEditMeetingForm(true)
      setShowAddForm(false)
    } catch (error) {
      toast.error("Failed to fetch meeting details")
    }
  }

  /**
   * Handles meeting delete action
   */
  const handleDeleteMeeting = async () => {
    setLoading(true)
    try {
      if (deleteMeetingId === null) return
      const delRes = await DeleteRequest(
        `lead/deleteMeeting/${deleteMeetingId}`,
        { headers },
        extras,
      )
      if (delRes.status === "fail" || delRes.success === false) {
        setLoading(false)
        toast.error(delRes.message)
        return
      }
      setLoading(false)
      toast.success(delRes.message)
      setConfirmDeleteMeeting(false)
      setDeleteMeetingId(null)
      meetingRefetch()
    } catch (error) {
      setLoading(false)
      toast.error("Failed to delete meeting")
    }
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

  // Get the number of days in a month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const clampStartToMonthWindow = (dateObj) => {
    const daysInMonth = getDaysInMonth(dateObj)
    const maxStart = Math.max(1, daysInMonth - 6) // last window start that still fits 7 days
    const start = Math.min(dateObj.getDate(), maxStart)
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), start)
  }

  // Get visible days (7 days starting from visibleStartDate)
  const getVisibleDays = () => {
    const days = []
    const year = visibleStartDate.getFullYear()
    const month = visibleStartDate.getMonth()
    const startDate = visibleStartDate.getDate()

    for (let i = 0; i < 7; i++) {
      const date = new Date(year, month, startDate + i)
      days.push({
        day: dayNames[date.getDay()],
        date: date.getDate(),
        fullDate: date,
        isCurrentMonth: date.getMonth() === currentDate.getMonth(),
      })
    }
    return days
  }

  // Handle scrolling left (previous days)
  const handlePrevious = () => {
    const year = visibleStartDate.getFullYear()
    const month = visibleStartDate.getMonth()
    const newDate = new Date(year, month, visibleStartDate.getDate() - 7)

    // Check if we're going before the start of current month
    if (newDate.getMonth() !== currentDate.getMonth() || newDate.getFullYear() !== currentDate.getFullYear()) {
      // Set to the first day of current month
      setVisibleStartDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1))
    } else {
      setVisibleStartDate(newDate)
    }
  }

  // Handle scrolling right (next days)
  const handleNext = () => {
    const year = visibleStartDate.getFullYear()
    const month = visibleStartDate.getMonth()
    const daysInMonth = getDaysInMonth(currentDate)
    const newDate = new Date(year, month, visibleStartDate.getDate() + 7)

    // Check if we're going beyond the current month
    if (newDate.getMonth() !== currentDate.getMonth() || newDate.getFullYear() !== currentDate.getFullYear()) {
      // Set to show the last 7 days of the month
      const lastVisibleStart = Math.max(1, daysInMonth - 6)
      setVisibleStartDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), lastVisibleStart))
    } else if (newDate.getDate() + 6 > daysInMonth) {
      // Adjust to show until end of month
      const lastVisibleStart = Math.max(1, daysInMonth - 6)
      setVisibleStartDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), lastVisibleStart))
    } else {
      setVisibleStartDate(newDate)
    }
  }

  // Check if previous button should be disabled
  const isPreviousDisabled = () => {
    return (
      visibleStartDate.getDate() === 1 &&
      visibleStartDate.getMonth() === currentDate.getMonth() &&
      visibleStartDate.getFullYear() === currentDate.getFullYear()
    )
  }

  // Check if next button should be disabled
  const isNextDisabled = () => {
    const daysInMonth = getDaysInMonth(currentDate)
    const lastVisibleDate = visibleStartDate.getDate() + 6
    return (
      lastVisibleDate >= daysInMonth &&
      visibleStartDate.getMonth() === currentDate.getMonth() &&
      visibleStartDate.getFullYear() === currentDate.getFullYear()
    )
  }

  // Handle month change
  // for other months, start from day 1 (keeps design intact)
  // Update the handleDayClick function
  const handleDayClick = (date) => {
    setSelectedDay(date)
    setSelectedDateForApi(new Date(date.getFullYear(), date.getMonth(), date))
  }

  // Update the changeMonth function to also update the selected date
  const changeMonth = (direction) => {
    const target = new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1)
    setCurrentDate(target)

    const today = new Date()
    if (target.getFullYear() === today.getFullYear() && target.getMonth() === today.getMonth()) {
      setSelectedDay(today.getDate())
      setVisibleStartDate(clampStartToMonthWindow(today))
      setSelectedDateForApi(today) // Set to today when current month
    } else {
      const firstDayOfMonth = 1
      setSelectedDay(firstDayOfMonth)
      setVisibleStartDate(new Date(target.getFullYear(), target.getMonth(), firstDayOfMonth))
      setSelectedDateForApi(new Date(target.getFullYear(), target.getMonth(), firstDayOfMonth))
    }
  }

  // Reset to current month when currentDate changes
  useEffect(() => {
    setVisibleStartDate((prev) => {
      if (prev.getMonth() === currentDate.getMonth() && prev.getFullYear() === currentDate.getFullYear()) {
        return prev
      }
      return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
    })
  }, [currentDate])

  useEffect(() => {
    const today = new Date()

    if (meetingFilter === "today") {
      setSelectedDay(today.getDate())
      setSelectedDateForApi(today)
    } else {
      setSelectedDay(null)
    }

    setVisibleStartDate(clampStartToMonthWindow(today))
  }, [meetingFilter])

  const visibleDays = getVisibleDays()

  // const cancelEdit = () => {
  //   setEditMeetingForm(false)
  //   setEditMeetingData(null)
  // reset({
  //   meeting_title: "",
  //   meeting_date: "",
  //   meeting_to: addThirtyMinutes(getCurrentTime()),
  //   meeting_from: getCurrentTime(),
  //   address: "",
  //   meeting_type: "inperson",
  //   meeting_link: "",
  //   description: "",
  //   meeting_status: "Scheduled",
  //   assign_to: customer.customer_details?.id
  // })
  //   setEmailRecipients(email ? [email] : [])
  //   setSelectedTimezone({
  //     value: "Asia/Kolkata",
  //     label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
  //     offset: 5.5,
  //     abbrev: "IST",
  //     altName: "India Standard Time",
  //   })
  // }
  // Add this useEffect to reset the warning when form is closed or edited
  useEffect(() => {
    if (!showAddForm && !editMeetingForm) {
      setHasConflict(false)
      setHasShownWarning(false)
    }
  }, [showAddForm, editMeetingForm])

  const getStatusColor = (status) => {
    switch (status) {
      case "conducted":
        return "bg-success"
      case "scheduled":
        return "bg-primary"
      case "cancelled":
        return "bg-danger"
      case "missed":
        return "bg-warning"
      default:
        return "bg-secondary"
    }
  }

  const handleCloseModal = () => {
    setShowAddForm(false)
    setEditMeetingForm(false)
    setEditMeetingData(null)
    // reset()
    reset({
      meeting_title: "",
      meeting_date: today,
      meeting_to: addThirtyMinutes(getCurrentTime()),
      meeting_from: getCurrentTime(),
      address: "",
      meeting_type: "inperson",
      meeting_link: "",
      description: "",
      meeting_status: "scheduled",
      assign_to: customer.customer_details?.id
    })
    setEmailRecipients(email ? [email] : [])
    setSelectedTimezone({
      value: "Asia/Kolkata",
      label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
      offset: 5.5,
      abbrev: "IST",
      altName: "India Standard Time",
    })
  }


  // Get lead status capsule color
  const getMeetingStatus = (status) => {
    switch (status?.toLowerCase()) {
      case "cancelled":
        return { backgroundColor: "#ffd1d1", color: "#d63031" }; // Light red background, dark red text
      case "missed":
        return { backgroundColor: "#fff4d1", color: "#fdcb6e" }; // Light blue background, dark blue text
      case "scheduled":
        return { backgroundColor: "#f1f3f4", color: "#636e72" }; // Light green background, dark green text
      case "conducted":
        return { backgroundColor: "#d1f7d1", color: "#00b894" }; // Light yellow background, dark yellow text
      // return { backgroundColor: "#fff4d1", color: "#fdcb6e" }; // Light yellow background, dark yellow text
      default:
        return { backgroundColor: "#f1f3f4", color: "#636e72" }; // Light gray background, dark gray text
    }
  }

  return (
    <div style={{ minHeight: "calc(100vh - 10.5rem)", maxHeight: "calc(100vh - 10.5rem)", overflowY: "auto", overflowX: "hidden", position: 'relative' }}>
      {/* Header Section */}
      <div className="d-flex align-items-center mb-1">
        {email !== undefined && (
          <div className="d-flex justify-content-between align-items-center gap-1 w-100">
            <div className="calendar-title">
              {/* <svg className="calendar-icon" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg> */}
              <h3 className=" mb-0" style={{ fontSize: "19px", fontWeight: 300 }}>Meetings</h3>
            </div>
            {/* create the dropdown for toggling between today's and all meetings */}
            <div className="d-flex gap-2 align-items-center">
              <div style={{ minWidth: "150px" }}>
                <Select
                  value={[
                    { value: "today", label: "Today" },
                    { value: "all", label: "All Meetings" },
                  ].find((option) => option.value === meetingFilter)}
                  onChange={(selectedOption) => {
                    const value = selectedOption?.value
                    setMeetingFilter(value)

                    if (value === "today") {
                      const today = new Date()
                      setSelectedDay(today.getDate())
                      setSelectedDateForApi(today)
                    } else {
                      setSelectedDay(null)
                    }
                  }}
                  options={[
                    { value: "today", label: "Today" },
                    { value: "all", label: "All Meetings" },
                  ]}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "30px",
                      height: "30px",
                      fontSize: "13px",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: "30px",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: "30px",
                      padding: "0 8px",
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                    }),
                  }}
                  isSearchable={false}
                />
              </div>
              <button
                className="btn btn-default-outline"
                style={{
                  fontSize: "13px",
                  height: "30px",
                  width: "auto",
                  gap: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "2px",
                  color: "#fff",
                }}
                onClick={() => {
                  reset()
                  setShowAddForm(true)
                  setEditMeetingForm(false)
                  setEditMeetingData(null)
                  setCurrentTimeValues()
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add
              </button>
            </div>
          </div>
        )}
      </div>

      <hr style={{ margin: "1rem 0rem" }} />

      {/* Meetings List */}
      {meetingIsLoading ? (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading meetings...</span>
          </div>
        </div>
      ) : (
        <ol style={{ listStyle: "none", overflowX: "scroll", scrollBehavior: "smooth", minHeight: "32rem", maxHeight: "32rem", scrollbarWidth: "thin" }} className="ms-0 listStyleForContact ps-0"
          onMouseEnter={(e) => {
            e.currentTarget.style.scrollbarColor = "#e0e0e0 white"
          }}
        >
          {/* Header */}
          <div className="calendar-header">
            <div className="calendar-month-nav">
              <button onClick={() => changeMonth(-1)} className="calendar-nav-btn">
                <ChevronLeft className="calendar-nav-icon" />
              </button>
              <div className="calendar-month-display">
                <span className="calendar-month-text">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </span>
              </div>
              <button onClick={() => changeMonth(1)} className="calendar-nav-btn">
                <ChevronRight className="calendar-nav-icon" />
              </button>
            </div>
          </div>

          {/* Calendar Days */}
          <div className="calendar-days-container">
            <button
              onClick={handlePrevious}
              disabled={isPreviousDisabled()}
              className={`calendar-day-nav-btn ${isPreviousDisabled() ? "calendar-nav-disabled" : ""}`}
            >
              <ChevronLeft className="calendar-nav-icon" />
            </button>

            {visibleDays.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  if (meetingFilter === "today") {
                    setSelectedDay(item.date)
                    setSelectedDateForApi(item.fullDate)
                  }
                }}
                style={{ lineHeight: 0.8 }}
                className={`calendar-day-btn ${meetingFilter === "today" && selectedDay === item.date
                  ? "calendar-day-selected"
                  : item.isCurrentMonth
                    ? "calendar-day-active"
                    : "calendar-day-inactive"
                  }`}
                disabled={!item.isCurrentMonth}
              >
                <span className="calendar-day-name">{item.day}</span>
                <span className="calendar-day-number">{item.date}</span>
              </button>
            ))}

            <button
              onClick={handleNext}
              disabled={isNextDisabled()}
              className={`calendar-day-nav-btn ${isNextDisabled() ? "calendar-nav-disabled" : ""}`}
            >
              <ChevronRight className="calendar-nav-icon" />
            </button>
          </div>

          {/* Meetings */}
          {/* Meetings */}
          {
            meetingGrant?.isView ?
              meetings?.length > 0 ?
                meetings?.map((meeting, index) => {
                  // Randomize between the two colors
                  const cardColors = ["#f5f4fe", "#fff5f4"]
                  const cardColor = cardColors[index % cardColors.length]

                  const meetingStatusColor = getMeetingStatus(meeting.meeting_status)
                  // Get invitees from meeting data
                  const invitees = meeting.invitees ? JSON.parse(meeting.invitees) : []
                  const visibleAvatarsCount = Math.min(invitees.length, 5)
                  const showMoreIndicator = invitees.length > 5
                  const moreCount = invitees.length - 5

                  // Random avatar images array
                  const avatarImages = ["img1", "img2", "img3"]

                  return (
                    <div
                      key={meeting.id}
                      className="meeting-card"
                      style={{
                        backgroundColor: cardColor,
                        marginBottom: "1.5rem"
                      }}
                    >
                      <div className="meeting-info">
                        <div className="d-flex justify-content-between">
                          <h4 className="meeting-title d-flex flex-column align-items-start">
                            <div className="title-row">
                              <span className="title-text">
                                {expandedTitles[meeting.id]
                                  ? meeting.meeting_title
                                  : truncate(meeting.meeting_title, 40)}
                              </span>

                              {meeting.meeting_title?.length > 40 && !expandedTitles[meeting.id] && (
                                <button
                                  className="toggle-btn more-btn"
                                  onClick={() => toggleTitle(meeting.id)}
                                  aria-label="Show more"
                                >
                                  ...
                                </button>
                              )}
                            </div>

                            {expandedTitles[meeting.id] && (
                              <div className="toggle-bottom">
                                <button
                                  className="toggle-btn less-btn"
                                  onClick={() => toggleTitle(meeting.id)}
                                  aria-label="Show less"
                                >
                                  Show Less
                                </button>
                              </div>
                            )}
                          </h4>
                          <Dropdown>
                            <style>
                              {
                                ` .no-arrow::after {
                              display: none !important;
                          }`

                              }
                            </style>
                            <Dropdown.Toggle variant="light" id="dropdown-basic" className="meeting-options-btn no-arrow p-0 m-0 bg-transparent border border-0">
                              <FontAwesomeIcon icon={faEllipsisVertical} />
                              <Dropdown.Menu className="z-3">
                                <Dropdown.Item
                                  onClick={() => {
                                    if (!meetingGrant?.isEdit) {
                                      toast.warning("You are not authorized for this action")
                                      return
                                    }
                                    handleEditMeeting(meeting.id, meeting.module_id)
                                  }}
                                  disabled={isCancelled}
                                >
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => {
                                    if (!meetingGrant?.isDelete) {
                                      toast.warning("You are not authorized for this action!")
                                      return
                                    }
                                    setDeleteMeetingId(meeting.id)
                                    setConfirmDeleteMeeting(true)
                                  }}
                                  disabled={isCancelled}
                                >
                                  Delete
                                </Dropdown.Item>
                                {meeting.meeting_status.toLowerCase() !== "cancelled" && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      if (!meetingGrant?.isEdit) {
                                        toast.warning("You are not authorized for this action!")
                                        return
                                      }
                                      handleCancelMeeting(meeting.id)
                                    }}
                                    disabled={isCancelled}
                                  >
                                    {isCancelled ? "Cancelling..." : "Cancel Meeting"}
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown.Toggle>
                          </Dropdown>
                        </div>
                        <span className="meeting-time">
                          Today <IconPoint style={{ stroke: 'grey', fill: "grey" }} size={12} ></IconPoint> {meeting.meeting_from} - {meeting.meeting_to}
                        </span>
                      </div>

                      <div className="meeting-details">
                        <div className="d-flex gap-2 align-items-center">
                          <div className="meeting-platform">
                            <img
                              src="https://www.gstatic.com/images/branding/product/1x/meet_2020q4_48dp.png"
                              alt="Google Meet"
                              className="meeting-platform-icon"
                            />
                            <span className="meeting-platform-text">
                              {meeting.meeting_type === "virtual" ? "Google Meet" : "In-Person"}
                            </span>

                          </div>
                          <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: meetingStatusColor.backgroundColor,
                            color: meetingStatusColor.color,
                            borderRadius: "12px",
                            padding: "4px 12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            minWidth: "80px",
                            textAlign: "center",
                            height: "20px",
                            textTransform: "capitalize"
                          }}>
                            {meeting.meeting_status}
                          </div>

                        </div>

                        <div className="meeting-attendees">
                          {invitees.slice(0, 5).map((invitee, index) => (
                            <img
                              key={invitee.id || index}
                              src={`https://i.pravatar.cc/40?img=${avatarImages[Math.floor(Math.random() * avatarImages.length)]}`}
                              alt={`Avatar ${index + 1}`}
                              className="meeting-avatar"
                              style={{ zIndex: visibleAvatarsCount - index }}
                              title={invitee.fullName || invitee.email}
                            />
                          ))}
                          {showMoreIndicator && (
                            <div className="meeting-more-attendees">
                              <span className="meeting-more-count">+{moreCount}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
                :
                <div
                  class="d-flex justify-content-center align-items-center"
                  style={{ position: "absolute", left: "30%" }}
                >
                  <img
                    src={nodata}
                    alt=""
                    style={{ width: "17rem", marginTop: "0" }}
                  />
                </div>
              :
              <UnauthViewModal tabName={"Meetings"} />
          }

          {confirmDeleteMeeting && (
            <DeleteModal
              show={confirmDeleteMeeting}
              onClose={setConfirmDeleteMeeting}
              onConfirm={handleDeleteMeeting}
              isLoading={loading}
              message="Are you sure you want to delete this item?"
            ></DeleteModal>
          )}
        </ol>
      )
      }

      {/* Bootstrap Modal for Add/Edit Meeting Form */}
      <Modal show={showAddForm || editMeetingForm} onHide={handleCloseModal} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editMeetingForm ? "Edit Meeting" : "Schedule New Meeting"}</Modal.Title>
        </Modal.Header>

        {/* Move the loading overlay to wrap the entire modal content */}
        <div style={{ position: "relative" }}>
          {isPosting && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                borderRadius: "0.375rem",
              }}
            >
              <div className="d-flex flex-column align-items-center">
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-primary fw-semibold">
                  {editMeetingForm ? "Updating meeting..." : "Creating meeting..."}
                </div>
              </div>
            </div>
          )}

          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <Form onSubmit={editMeetingForm ? handleSubmit(handleUpdateMeeting) : handleSubmit(onSubmit)} className="form-container">
              <Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className=" position-relative form-z-index mb-2">
                      <Form.Label className="">
                        Meeting Title <span className="text-danger">*</span>
                      </Form.Label>
                      <input
                        type="text"
                        className={`form-control `}
                        placeholder="Meeting title..."
                        {...register("meeting_title")}
                      />
                      {errors.meeting_title && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_title.message}</div>}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className=" position-relative form-z-index mb-2">
                      <Form.Label>
                        Meeting Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        className={`form-control `}
                        min={today}
                        {...register("meeting_date")}
                      ></Form.Control>
                    </Form.Group>
                    {errors.meeting_date && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_date.message}</div>}
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-2 form-z-index position-relative reactSelectedDiv">
                      <label >Meeting Type </label>
                      <Controller
                        name="meeting_type"
                        control={control}
                        style={{ minHeight: '38px', paddingTop: '0px' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={meetingTypeOptions}
                            placeholder="Select meeting type..."
                            classNamePrefix="react-select"
                            styles={customStyles}
                            value={meetingTypeOptions.find((option) => option.value === field.value)}
                            onChange={(selectedOption) => field.onChange(selectedOption.value)}
                          />
                        )}
                      />
                      {errors.meeting_type && (
                        <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_type.message}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className=" position-relative form-z-index mb-2">
                      <label className="form-label">
                        Time(From) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="time"
                        className={`form-control `}
                        {...register("meeting_from")}
                      />
                      {errors.meeting_from && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_from.message}</div>}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className=" position-relative form-z-index mb-2">
                      <label className="form-label">
                        Time(To) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="time"
                        className={`form-control `}
                        {...register("meeting_to")}
                      />
                      {errors.meeting_to && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_to.message}</div>}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className=" position-relative form-z-index mb-2">
                      <Form.Label className="w-auto" style={{ marginBottom: 1 }}>
                        Assign To
                      </Form.Label>
                      <Controller
                        name="assign_to"
                        control={control}
                        render={({ field }) => (
                          <Select
                            styles={customStyles}
                            {...field}
                            className="input-box-select"
                            options={leadOwner.map((lead) => ({
                              value: lead.leadId,
                              label: lead.leadName,
                            }))}
                            value={
                              leadOwner.find((lead) => lead.leadId === field.value)
                                ? {
                                  value: field.value,
                                  label: leadOwner.find((lead) => lead.leadId === field.value)?.leadName,
                                }
                                : leadOwner[0]
                                  ? {
                                    value: leadOwner[0].leadId,
                                    label: leadOwner[0].leadName,
                                  }
                                  : null
                            }
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption?.value || customer.customer_details.id)
                            }}
                            isClearable={true}
                            classNamePrefix="react-select"
                          />
                        )}
                      />
                      {/* Warning text appears here */}
                      {hasConflict && (
                        <div className="text-warning mt-1" style={{ fontSize: "12px" }}>
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                          {warningMessage !== "" && warningMessage}
                        </div>
                      )}
                      {errors.assign_to && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.assign_to.message}</div>}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className=" position-relative form-z-index mb-3 ">
                      <Form.Label className="w-auto" style={{ marginBottom: 1 }}>
                        Meeting Status
                      </Form.Label>
                      {/* <label className="form-label position-relative">Meeting Status </label> */}
                      <Controller
                        name="meeting_status"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={meetingStatusOptions}
                            styles={customStyles}
                            classNamePrefix="react-select"
                            placeholder="Select status..."
                            value={meetingStatusOptions.find((option) => option.value === field.value)}
                            onChange={(selectedOption) => field.onChange(selectedOption.value)}

                          />
                        )}
                      />
                      {errors.meeting_status && (
                        <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_status.message}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group className=" position-relative form-z-index mb-3" controlId="formTimezone">
                      <div className="d-flex flex-column gap-1">
                        <Form.Label className="w-auto" style={{ marginBottom: 1 }}>
                          Select Timezone <span className="text-danger">*</span>
                        </Form.Label>
                        <div style={{ fontSize: "14px" }}>
                          <TimezoneSelect
                            styles={customStyles}
                            timezones={{
                              ...allTimezones,
                              "America/Lima": "Pittsburgh",
                              "Europe/Berlin": "Frankfurt",
                            }}
                            value={selectedTimezone}
                            onChange={setSelectedTimezone}
                          />
                        </div>
                      </div>
                    </Form.Group>
                  </Col>

                  {/* email dropdown */}
                  <Form.Group className=" position-relative form-z-index mb-3" >
                    <div className="">
                      {/* <label className="form-label">
                      Invitees <span className="text-danger">*</span>
                    </label> */}
                      <Form.Label className="w-auto" style={{ marginBottom: 1, marginLeft: '11px' }}>
                        Invitees <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className="border rounded p-2"
                        style={{ minHeight: "40px", cursor: "text" }}
                        onClick={() => document.getElementById("email-input").focus()}
                      >
                        <div
                          onClick={() => setShowEmailDropdown(true)}
                          className="d-flex flex-wrap gap-1 align-items-center"
                        >
                          {emailRecipients.map((e, index) => (
                            <span
                              key={index}
                              className="badge bg-primary d-flex align-items-center gap-1 mt-3"
                              style={{ fontSize: "12px", padding: "4px 8px" }}
                            >
                              {e}
                              {e !== email && (
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  style={{ cursor: "pointer", fontSize: "10px" }}
                                  onClick={() => removeEmailTag(e)}
                                />
                              )}
                            </span>
                          ))}
                          <input
                            id="email-input"
                            type="text"
                            className="border-0 flex-grow-1"
                            style={{ outline: "none", minWidth: "200px", background: "transparent" }}
                            placeholder={emailRecipients.length === 0 ? "Press ENTER to add email..." : "Add more emails..."}
                            value={currentEmailInput}
                            onChange={handleEmailInputChange}
                            onKeyDown={handleEmailInputKeyDown}
                            onFocus={() => currentEmailInput.length > 0}
                          />
                        </div>
                      </div>

                      {showEmailDropdown && (
                        <div
                          className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                          style={{ zIndex: 1000, maxHeight: "300px", overflowY: "auto" }}
                          ref={emailDropdownRef}
                        >
                          {isLoadingEmails ? (
                            <div className="p-3 text-center text-muted">
                              <small>Loading emails...</small>
                            </div>
                          ) : (
                            <>
                              {Object.entries(emailOptions).map(
                                ([categoryName, contacts]) =>
                                  contacts.length > 0 && (
                                    <div key={categoryName}>
                                      <div className="px-3 py-2 bg-light border-bottom">
                                        <small className="fw-bold text-muted">
                                          {categoryName.toUpperCase()} ({contacts.length})
                                        </small>
                                      </div>
                                      {contacts.map((contact, index) => (
                                        <div
                                          key={`${categoryName}-${contact.id || index}`}
                                          className="px-3 py-2 hover-bg-light cursor-pointer border-bottom"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => addEmailTag(contact?.email)}
                                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
                                          onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                                        >
                                          <div className="d-flex align-items-center">
                                            <div>
                                              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                                                {contact.fullName}
                                              </div>
                                              <small className="text-muted">{contact.email}</small>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ),
                              )}

                              {Object.values(emailOptions).every((contacts) => contacts.length === 0) &&
                                !isLoadingEmails && (
                                  <div className="p-3 text-center text-muted">
                                    <small>No emails found. Type to search or press Enter to add manually.</small>
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                      )}
                      {emailRecipients.length === 0 && (
                        <div className="invalid-feedback d-block">At least one invitee is required</div>
                      )}
                    </div>
                  </Form.Group>

                  {meetingType === "virtual" ? (

                    // <div className="col-12 ">
                    <Form.Group className="col-12 position-relative form-z-index mb-3">
                      {/* <label className="form-label"> */}

                      <Form.Label className="w-auto" style={{ marginBottom: 1, marginLeft: '11px' }}>
                        Meeting Link <span className="text-danger">*</span>                        </Form.Label>
                      {/* </label> */}
                      <input
                        type="text"
                        className={`form-control `}
                        placeholder="Meeting link (Google Meet/Zoom URL)..."
                        {...register("meeting_link")}
                      />
                      {errors.meeting_link && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.meeting_link.message}</div>}
                    </Form.Group>
                    // </div>
                  ) : (
                    <div className="col-12 ">
                      <Form.Group className="col-12 position-relative form-z-index mb-3">
                        <Form.Label className="w-auto" style={{ marginBottom: 1 }}>
                          Location <span className="text-danger">*</span>                      </Form.Label>
                        <input
                          type="text"
                          className={`form-control`}
                          placeholder="Address..."
                          {...register("address")}
                        />
                        {errors.address && <div className="text-danger mt-1" style={{ fontSize: '12px', marginLeft: '2px' }}>{errors.address.message}</div>}
                      </Form.Group>
                    </div>
                  )}

                  <Form.Group className="col-12 position-relative form-z-index mb-3">
                    {/* <label className="form-label">
                      Description <span className="text-danger">*</span>
                    </label> */}
                    <Form.Label className="w-auto" style={{ marginBottom: 1, marginLeft: "11px" }}>
                      Description                        </Form.Label>
                    <textarea
                      className={`form-control `}
                      rows={3}
                      placeholder="Meeting description..."
                      {...register("description")}
                    />
                  </Form.Group>
                </Row>
              </Row>

              <div className="d-flex gap-2 justify-content-end" style={{ paddingRight: '25px' }}>
                <Button type="button" className="btn btn-default-outline" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" className="btn btn-default" disabled={isPosting}>
                  {editMeetingForm ? "Update Meeting" : "Schedule Meeting"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </div>
      </Modal>
    </div >
  )
}

const styles = {
  filterbuttons: {
    fontSize: "13px",
    height: "33px",
    width: "33px",
    maxHeight: 33,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttons: {
    color: "#fff",
    fontSize: "13px",
    height: "34px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}